import { useCallback, useEffect, useState } from "react";
import {
  CopyIcon,
  CheckIcon,
  LinkIcon,
  ArrowsClockwiseIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { ErrorBanner, Field } from "../crm/Form";
import api, { formatApiError } from "../../lib/api";

const SCOPES = [
  { value: "owner_safe", label: "Owner-safe (recommended)", desc: "Hides internal findings & recommendations" },
  { value: "full", label: "Full report", desc: "Includes everything — internal forwarding only" },
];

const EXPIRY_PRESETS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days (default)" },
  { value: 90, label: "90 days" },
  { value: 365, label: "1 year" },
  { value: 0, label: "Never expires" },
];

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function ShareLinkPanel({ workOrderId, reportStatus }) {
  const [shares, setShares] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState("owner_safe");
  const [expiry, setExpiry] = useState(30);
  const [latest, setLatest] = useState(null); // newly created — has plaintext token + url
  const [copied, setCopied] = useState(null);

  const isFinalized = reportStatus === "finalized";

  const load = useCallback(async () => {
    if (!isFinalized) {
      setShares([]);
      return;
    }
    setErr("");
    try {
      const { data } = await api.get(
        `/work-orders/${workOrderId}/diagnostic-report/shares`
      );
      setShares(data || []);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [workOrderId, isFinalized]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    setErr("");
    try {
      const body = { scope, expires_in_days: expiry === 0 ? null : expiry };
      const { data } = await api.post(
        `/work-orders/${workOrderId}/diagnostic-report/share`,
        body
      );
      setLatest(data);
      toast.success("Share link created");
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(id);
      setTimeout(() => setCopied(null), 1800);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy — please copy manually.");
    }
  };

  const revoke = async (share) => {
    if (
      !window.confirm(
        "Revoke this link? Anyone holding it will see a 'no longer active' message."
      )
    )
      return;
    try {
      await api.delete(`/diagnostic-report-shares/${share.id}`);
      toast.success("Link revoked");
      if (latest && latest.id === share.id) setLatest(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!isFinalized) {
    return (
      <div
        data-testid="share-panel-locked"
        className="text-xs text-slate-500"
      >
        Sharing becomes available after the report is finalized.
      </div>
    );
  }

  return (
    <div data-testid="share-panel" className="space-y-5">
      <ErrorBanner message={err} />

      {/* Newly-created token banner — token is shown only here, only once */}
      {latest && (
        <div
          data-testid="share-just-created"
          className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2"
        >
          <div className="text-[11px] uppercase tracking-wider text-amber-900 font-bold">
            New link · copy now (won't be shown again)
          </div>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={latest.url}
              data-testid="share-just-created-url"
              className="flex-1 text-[12px] font-data bg-white border border-amber-300 rounded-md px-2 py-1.5 text-slate-900"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              onClick={() => copy(latest.url, latest.id)}
              data-testid="share-just-created-copy"
              className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-amber-700"
            >
              {copied === latest.id ? (
                <>
                  <CheckIcon size={12} weight="bold" /> Copied
                </>
              ) : (
                <>
                  <CopyIcon size={12} weight="bold" /> Copy
                </>
              )}
            </button>
          </div>
          <div className="text-[11px] text-amber-900">
            Scope: <strong>{latest.scope}</strong> · Expires:{" "}
            <strong>{latest.expires_at ? fmt(latest.expires_at) : "never"}</strong>
          </div>
        </div>
      )}

      {/* Create new */}
      <div className="rounded-md border border-slate-200 p-4 space-y-3 bg-white">
        <div className="font-heading text-sm font-semibold text-slate-900">
          Create a share link
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Scope">
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              data-testid="share-scope-select"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
            >
              {SCOPES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Expires">
            <select
              value={expiry}
              onChange={(e) => setExpiry(Number(e.target.value))}
              data-testid="share-expiry-select"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
            >
              {EXPIRY_PRESETS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="text-[11px] text-slate-500">
          {SCOPES.find((s) => s.value === scope)?.desc}
        </div>
        <button
          type="button"
          onClick={create}
          disabled={busy}
          data-testid="share-create-button"
          className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-[13px] font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          <LinkIcon size={14} weight="duotone" />
          {busy ? "Creating…" : "Create Share Link"}
        </button>
      </div>

      {/* Existing shares */}
      <div data-testid="share-list" className="space-y-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          <ArrowsClockwiseIcon size={12} weight="bold" />
          Existing links
          <span className="text-slate-400">· {shares.length}</span>
        </div>
        {shares.length === 0 && (
          <div className="text-xs text-slate-500">No share links yet.</div>
        )}
        {shares.map((s) => {
          const revoked = !!s.revoked_at;
          const expired =
            s.expires_at && new Date(s.expires_at) <= new Date();
          const inactive = revoked || expired;
          return (
            <div
              key={s.id}
              data-testid={`share-row-${s.id}`}
              className={`flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 ${
                inactive
                  ? "border-slate-200 bg-slate-50 opacity-70"
                  : "border-slate-200 bg-white"
              }`}
            >
              <div className="text-xs text-slate-700 min-w-0">
                <div className="font-data text-[11px] text-slate-500">
                  {s.id.slice(0, 8)} · {s.scope}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Created {fmt(s.created_at)}
                  {s.created_by_name ? ` by ${s.created_by_name}` : ""} ·
                  Expires{" "}
                  {s.expires_at ? fmt(s.expires_at) : "never"} ·
                  Accesses {s.access_count || 0}
                </div>
                {revoked && (
                  <div className="text-[11px] text-red-700 mt-0.5">
                    Revoked {fmt(s.revoked_at)}
                  </div>
                )}
                {expired && !revoked && (
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Expired
                  </div>
                )}
              </div>
              {!inactive && (
                <button
                  type="button"
                  onClick={() => revoke(s)}
                  data-testid={`share-revoke-${s.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:text-red-600 hover:border-red-200"
                >
                  <TrashIcon size={11} weight="bold" />
                  Revoke
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
